'use client'

import { useCallback, useMemo, useRef, useState } from 'react'
import { useParams } from 'next/navigation'
import type { Reservation } from '@/root/shared-types/reservation/reservation-types'
import { dateFormats, testIds } from '@/root/shared-consts'
import { dateToFormat } from '@/root/shared-helpers/date/dateHelpers'
import {
  useAppConfig,
  useCustomRouter,
  useGetAvailableDates,
  useGetTranslations,
  useErrorBanner,
} from '@/root/shared-hooks'
import type {
  EditReservationCustomerTabHandle,
  EditReservationTicketsLeg,
  ReservationWithModification,
  TripDirection,
} from '@/root/modules/shared/EditReservation/types'
import { MODIFY_ERROR_MESSAGE_KEYS } from '@/root/modules/shared/EditReservation/consts'
import { commit, revert } from '@/app/actions/reservation-modification'
import { usePaymentFlow } from '@/root/modules/shared/EditReservation/hooks/usePaymentFlow'
import { useRouteEditState } from '@/root/modules/shared/EditReservation/hooks/useRouteEditState'
import { useTicketEditState } from '@/root/modules/shared/EditReservation/hooks/useTicketEditState'
import { useEditNavigation } from '@/root/modules/shared/EditReservation/hooks/useEditNavigation'
import {
  hasReservationChanged,
  getLegCancellationState,
  isLegHidden,
  isLegAdminCancelledEditable,
  countReservationItems,
  formatActionError,
  handleStaleServerAction,
  resolveFinishRouting,
} from '@/root/modules/shared/EditReservation/utils'

interface UseEditReservationContentParams {
  /**
   * Last committed state of the reservation. Must NOT be derived from `reservation`: on a reload
   * of an open session `start()` returns the staged state, so a self-diff would read as "no
   * changes" and hide both the cart highlight and any new refund offer.
   */
  committedReservation: Reservation
  /** Reservation including the uncommitted changes of the open modification session. */
  reservation: ReservationWithModification
}

/** The orchestration of the edit page: tabs, legs, session, finish, cancel and payment; client markup renders it. */
export const useEditReservationContent = ({ reservation, committedReservation }: UseEditReservationContentParams) => {
  const { getTranslation } = useGetTranslations()
  const { formatMoney } = useAppConfig()
  const { locale, push } = useCustomRouter()
  const params = useParams()
  const reservationToken = typeof params?.reservationToken === 'string' ? params?.reservationToken : undefined

  // Local state for current reservation (updated after successful modify)
  const [currentReservation, setCurrentReservation] = useState<ReservationWithModification>(reservation)
  // Session expiry — set by start(), updated by prolong()
  const [sessionExpireAt, setSessionExpireAt] = useState(reservation.expireAt)

  const sailPackages = useMemo(() => currentReservation.sailPackages ?? [], [currentReservation.sailPackages])

  const firstSailPackage = sailPackages[0]
  const secondSailPackage = sailPackages[1]
  const firstSailRef = firstSailPackage?.sailRefs?.[0]
  const secondSailRef = secondSailPackage?.sailRefs?.[0]

  // Leg cancellation state — recomputed from currentReservation after each modification
  const firstLegCancellation = useMemo(
    () => getLegCancellationState(firstSailPackage, firstSailRef),
    [firstSailPackage, firstSailRef]
  )
  const secondLegCancellation = useMemo(
    () => getLegCancellationState(secondSailPackage, secondSailRef),
    [secondSailPackage, secondSailRef]
  )
  const hideFirstLeg = isLegHidden(firstLegCancellation)
  const hideSecondLeg = isLegHidden(secondLegCancellation)
  const firstLegAdminCancelled = isLegAdminCancelledEditable(firstLegCancellation)
  const secondLegAdminCancelled = isLegAdminCancelledEditable(secondLegCancellation)

  const fallbackTicketSailPackage = useMemo(
    () => currentReservation.sailPackages?.find((pkg) => (pkg.sailRefs?.length ?? 0) > 0),
    [currentReservation.sailPackages]
  )
  const fallbackTicketSailRefId = fallbackTicketSailPackage?.sailRefs?.[0]?.sailRefId
  const ticketSailPackage = firstSailPackage?.sailRefs?.length ? firstSailPackage : fallbackTicketSailPackage
  const ticketSailPackageSeqN =
    firstSailPackage?.seqN ?? ticketSailPackage?.seqN ?? currentReservation.items?.[0]?.sailPackageSeqN ?? 1
  const additionalTicketSailPackages = useMemo(
    () =>
      sailPackages
        .filter((pkg) => pkg !== ticketSailPackage && (pkg.sailRefs?.length ?? 0) > 0)
        .map((pkg) => ({
          seqN: pkg.seqN ?? 1,
          code: pkg.code,
          sailRefIds: pkg.sailRefs.map((sailRef) => sailRef.sailRefId),
        })),
    [sailPackages, ticketSailPackage]
  )

  // Fetch available dates for the sail packages
  const availableDatesOutbound = useGetAvailableDates({ sailPackage: firstSailPackage?.code ?? '' })
  const availableDatesReturn = useGetAvailableDates({ sailPackage: secondSailPackage?.code ?? '' })

  // Error handling via shared provider
  const { errorHandler, clearError } = useErrorBanner()

  const [isFinishing, setIsFinishing] = useState(false)
  const [hasCustomerChanges, setHasCustomerChanges] = useState(false)
  const customerTabRef = useRef<EditReservationCustomerTabHandle>(null)
  const [showProlongModal, setShowProlongModal] = useState(false)

  const showError = useCallback(
    (message: string, error?: unknown) => {
      errorHandler?.({ error, time: new Date(), message })
    },
    [errorHandler]
  )

  const handleHookError = useCallback(
    (message: string | null) => {
      if (message) showError(message)
      else clearError?.()
    },
    [showError, clearError]
  )

  const routeState = useRouteEditState({
    currentReservation,
    reservationToken,
    locale,
    firstSailPackage,
    firstSailRef,
    secondSailPackage,
    secondSailRef,
    committedSailPackages: committedReservation.sailPackages,
    fallbackTicketSailRefId,
    availableDatesOutbound,
    availableDatesReturn,
    getTranslation,
    onReservationUpdated: setCurrentReservation,
    onError: handleHookError,
  })

  const ticketState = useTicketEditState({
    reservationToken,
    currentReservation,
    ticketSailPackageSeqN,
    additionalTicketSailPackages,
    locale,
    getTranslation,
    onReservationUpdated: setCurrentReservation,
    onError: handleHookError,
  })

  const isModifying = routeState.isSaving || ticketState.isSaving || ticketState.isOperationPending

  const resetCustomerForm = useCallback(() => customerTabRef.current?.resetForm(), [])

  const navigation = useEditNavigation({
    clearError,
    hasCustomerChanges,
    hasUnsavedOutboundForms: ticketState.hasUnsavedOutboundForms,
    hasUnsavedPassengerForms: ticketState.hasUnsavedPassengerForms,
    hasUnsavedReturnForms: ticketState.hasUnsavedReturnForms,
    isModifying,
    resetCustomerForm,
  })

  const hasSaved = useMemo(
    () => hasReservationChanged(currentReservation, committedReservation),
    [currentReservation, committedReservation]
  )

  const canFinish =
    hasSaved && !routeState.hasRouteChanges && !hasCustomerChanges && !ticketState.hasUnsavedPassengerForms

  const toPayAmount = currentReservation.paymentInfo?.toPayAmount ?? 0
  const toRefundAmount = currentReservation.paymentInfo?.toRefundAmount ?? 0
  const currentFinishRouting = useMemo(
    () => resolveFinishRouting(committedReservation.paymentInfo, currentReservation.paymentInfo),
    [committedReservation.paymentInfo, currentReservation.paymentInfo]
  )
  const isRefundOffered = hasSaved && currentFinishRouting.scenario === 'refund'

  // Reservation item counts for capacity checks — per direction
  const outboundCounts = useMemo(
    () => countReservationItems(currentReservation.items ?? [], firstSailPackage?.seqN),
    [currentReservation.items, firstSailPackage?.seqN]
  )
  const returnCounts = useMemo(
    () => countReservationItems(currentReservation.items ?? [], secondSailPackage?.seqN),
    [currentReservation.items, secondSailPackage?.seqN]
  )

  const handleCancel = useCallback(async () => {
    if (reservationToken) {
      try {
        const revertResult = await revert(reservationToken, locale)
        if (!revertResult.success) {
          showError(getTranslation('editErrorCancelFailed'))
          return
        }
        clearError?.()
        push(`/${locale}/ticket/${reservationToken}`)
      } catch (error) {
        if (handleStaleServerAction({ error, getTranslation, onError: showError })) {
          return
        }

        showError(getTranslation('editErrorCancelFailed'), error)
      }
    }
  }, [locale, push, reservationToken, getTranslation, showError, clearError])

  const handleExpiringSoon = useCallback(() => {
    setShowProlongModal(true)
  }, [])

  const directionTabs = useMemo(
    (): { date: string; id: TripDirection; testId: string; title: string }[] => [
      {
        id: 'outbound',
        title: firstSailRef?.routeLegTitle ?? getTranslation('editTicketsOutboundLabel'),
        date: firstSailRef ? dateToFormat(firstSailRef.departureDate, dateFormats.DD_DOT_MM_YYYY, locale) : '',
        testId: testIds.edit.directionOutbound,
      },
      {
        id: 'return',
        title: secondSailRef?.routeLegTitle ?? getTranslation('editTicketsReturnLabel'),
        date: secondSailRef ? dateToFormat(secondSailRef.departureDate, dateFormats.DD_DOT_MM_YYYY, locale) : '',
        testId: testIds.edit.directionReturn,
      },
    ],
    [firstSailRef, secondSailRef, locale, getTranslation]
  )

  const payment = usePaymentFlow({
    locale,
    reservationToken,
    onError: handleHookError,
    onRedirect: push,
    onFinishingChange: setIsFinishing,
    getTranslation,
    toPayAmount,
    ownerCompanyId: currentReservation.reservationOwner?.companyId,
  })

  const handleFinish = useCallback(async () => {
    if (!reservationToken) return

    setIsFinishing(true)
    clearError?.()

    const reservationId = currentReservation.reservationId

    // Commit first — backend response is the source of truth for what to do next
    let commitResult: Awaited<ReturnType<typeof commit>>
    try {
      commitResult = await commit(reservationToken, locale)
    } catch (error) {
      if (handleStaleServerAction({ error, getTranslation, onError: showError })) {
        setIsFinishing(false)
        return
      }
      throw error
    }

    if (!commitResult.success) {
      const messageKey =
        (commitResult.error.code && MODIFY_ERROR_MESSAGE_KEYS[commitResult.error.code]) || 'editErrorFinishFailed'
      const errorMessage = formatActionError(getTranslation(messageKey), commitResult.error.code, getTranslation)
      showError(errorMessage, { status: commitResult.error.status })
      setIsFinishing(false)
      return
    }

    const committedToken = commitResult.data.token ?? reservationToken
    const finishRouting = resolveFinishRouting(
      committedReservation.paymentInfo,
      currentReservation.paymentInfo,
      commitResult.data.paymentInfo
    )

    switch (finishRouting.scenario) {
      case 'payment':
        await payment.actions.fetchAndProcessPayment(reservationId, committedToken)
        payment.showPaymentUI()
        return

      case 'refund':
        setIsFinishing(false)
        push(
          `/${locale}/edit-reservation/${committedToken}/refund-confirm?refundAmount=${finishRouting.refundAmount}&reservationId=${reservationId}`
        )
        return

      case 'ticket':
        push(`/${locale}/ticket/${committedToken}`)
        return
    }
  }, [
    reservationToken,
    push,
    locale,
    getTranslation,
    currentReservation.reservationId,
    currentReservation.paymentInfo,
    committedReservation.paymentInfo,
    showError,
    clearError,
    payment,
  ])

  const routeLegs = [
    !hideFirstLeg && firstSailPackage && firstSailRef && routeState.outbound
      ? {
          direction: 'outbound' as const,
          sailPackage: firstSailPackage,
          sailRef: firstSailRef,
          edit: routeState.outbound,
          counts: outboundCounts,
          isSaving: routeState.savingRoute === 'outbound',
          hasDividerBefore: false,
        }
      : null,
    !hideSecondLeg && secondSailPackage && secondSailRef && routeState.return
      ? {
          direction: 'return' as const,
          sailPackage: secondSailPackage,
          sailRef: secondSailRef,
          edit: routeState.return,
          counts: returnCounts,
          isSaving: routeState.savingRoute === 'return',
          hasDividerBefore: !hideFirstLeg,
        }
      : null,
  ].filter((leg) => leg !== null)

  // Two legs with direction tabs; otherwise one leg: the return leg if the first is hidden, else the first
  const showDirectionTabs = Boolean(
    ticketState.isRoundTrip &&
    ticketState.returnSailPackage &&
    ticketState.handleSavePassengerInfoReturn &&
    !hideFirstLeg &&
    !hideSecondLeg
  )
  const ticketLegBase = {
    reservation: currentReservation,
    reservationToken: reservationToken ?? '',
    locale,
    disabled: isModifying || isFinishing,
    onPendingChange: ticketState.setIsOperationPending,
    onError: ticketState.handleTicketError,
    onErrorClear: ticketState.clearError,
    onTicketSaved: ticketState.handleTicketSaved,
  }
  const outboundTicketLeg: EditReservationTicketsLeg = {
    direction: 'outbound',
    isShown: !showDirectionTabs || navigation.activeDirection === 'outbound',
    key: showDirectionTabs ? 'outbound' : 'single',
    props: {
      ...ticketLegBase,
      sailPackageSeqN: ticketSailPackageSeqN,
      sailRefId: routeState.ticketSailRefId,
      sailPackageCode: ticketSailPackage?.code ?? '',
      sailRefIds: ticketSailPackage?.sailRefs.map((sailRef) => sailRef.sailRefId) ?? [],
      isSailCancelled: firstLegAdminCancelled,
      onUnsavedFormsChange: ticketState.setHasUnsavedOutboundForms,
      onSavePassengerInfo: ticketState.handleSavePassengerInfoOutbound,
    },
  }
  const returnTicketLeg: EditReservationTicketsLeg | null = ticketState.returnSailPackage
    ? {
        direction: 'return',
        isShown: !showDirectionTabs || navigation.activeDirection === 'return',
        key: showDirectionTabs ? 'return' : 'single',
        props: {
          ...ticketLegBase,
          sailPackageSeqN: ticketState.returnSailPackage.seqN,
          sailRefId: routeState.returnTicketSailRefId,
          sailPackageCode: ticketState.returnSailPackage.code,
          sailRefIds: ticketState.returnSailPackage.sailRefIds,
          isSailCancelled: secondLegAdminCancelled,
          onUnsavedFormsChange: ticketState.setHasUnsavedReturnForms,
          // Single-leg view without a return handler falls back to the outbound one, as before the move
          onSavePassengerInfo: ticketState.handleSavePassengerInfoReturn ?? ticketState.handleSavePassengerInfoOutbound,
        },
      }
    : null
  const ticketLegs: EditReservationTicketsLeg[] =
    showDirectionTabs && returnTicketLeg
      ? [outboundTicketLeg, returnTicketLeg]
      : [hideFirstLeg && returnTicketLeg ? returnTicketLeg : outboundTicketLeg]

  return {
    locale,
    getTranslation,
    reservation: currentReservation,
    reservationToken,
    isModifying,
    navigation: { ...navigation, directionTabs },
    session: {
      expireAt: sessionExpireAt,
      isProlongOpen: showProlongModal,
      openProlong: handleExpiringSoon,
      closeProlong: () => setShowProlongModal(false),
      onSessionExtended: setSessionExpireAt,
      onExpiredRedirect: () => push(`/${locale}/ticket/${reservationToken}`),
      onError: showError,
    },
    route: { legs: routeLegs },
    tickets: { legs: ticketLegs, showDirectionTabs },
    customer: { ref: customerTabRef, onDirtyChange: setHasCustomerChanges, onSaved: setCurrentReservation },
    cart: {
      committedReservation,
      hasSaved,
      hideFirstLeg,
      hideSecondLeg,
      isRefundOffered,
      toPayAmount,
      toPayBannerText: getTranslation('editBillingAdditionalPaymentBanner', { amount: formatMoney(toPayAmount) }),
      toRefundBannerText: getTranslation('editBillingRefundBanner', { amount: formatMoney(toRefundAmount) }),
    },
    finish: { canFinish, isFinishing, hasExtraPayment: toPayAmount > 0, handleFinish, handleCancel },
    payment: {
      ...payment,
      termsText: currentReservation.terms?.find((term) => term.language === locale)?.ticketTerms,
      pay: () => payment.actions.pay(currentReservation.reservationId),
    },
  }
}
